/**
 * CsvExportService
 * 
 * Service for exporting data_id based data to CSV format.
 * Developer tier exclusive feature.
 */

import { ChartService, ToolDataResponse } from './ChartService';
import { buildEnglishLabels } from '../features/data-labeling';
import config from '../config/api';
import logger from '../logging/logger';

export interface CsvExportResult {
  success: boolean;
  filename: string;
  error?: string;
}

export class CsvExportService {
  private chartService: ChartService;
  private baseUrl = config.apiUrl;

  constructor() {
    this.chartService = new ChartService();
  }

  /**
   * Check if user is on developer tier
   */
  async isDeveloperTier(userUuid: string, token: string): Promise<boolean> {
    try {
      const response = await fetch(
        `${this.baseUrl}/analytics/quota/${userUuid}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        logger.error('CsvExportService: Failed to fetch user tier:', response.status);
        return false;
      }

      const data = await response.json();
      const tier = data.tier?.toLowerCase();
      const isDev = tier === 'developer';

      logger.info('CsvExportService: Tier check result:', { tier, isDev });

      return isDev;
    } catch (error) {
      logger.error('CsvExportService: Error checking developer tier:', error);
      return false;
    }
  }

  /**
   * Convert columnar data to CSV string
   * Data format: { dates?: string[], Dates?: string[], [valueKey]: number[] | string[] }
   */
  convertToCsv(data: ToolDataResponse): string {
    // Get all keys that have array values
    const columns = Object.keys(data).filter(
      key => Array.isArray(data[key]) && data[key]!.length > 0
    );

    if (columns.length === 0) {
      return '';
    }

    // Normalize column names (use lowercase for CSV headers)
    const normalizedColumns = columns.map(col => 
      col === 'Dates' ? 'dates' : col
    );

    // Get the maximum row count
    const rowCount = Math.max(
      ...columns.map(col => (data[col] as any[]).length)
    );

    // Build CSV header
    const header = normalizedColumns.join(',');

    // Build CSV rows
    const rows: string[] = [];
    for (let i = 0; i < rowCount; i++) {
      const rowValues = columns.map(col => {
        const value = (data[col] as any[])[i];
        if (value === null || value === undefined) {
          return '';
        }
        // Escape strings with commas or quotes
        if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
          return `"${value.replace(/"/g, '""')}"`;
        }
        return String(value);
      });
      rows.push(rowValues.join(','));
    }

    return [header, ...rows].join('\n');
  }

  /**
   * Trigger browser download of CSV file
   */
  downloadCsv(csvContent: string, filename: string = 'quantic_datasheet.csv'): void {
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.style.display = 'none';
    
    document.body.appendChild(link);
    link.click();
    
    // Cleanup
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  /**
   * Export data by data_id to CSV and trigger download
   */
  async exportToCsv(
    sessionId: string,
    dataId: string,
    userEmail: string,
    token: string,
    filename: string = 'quantic_datasheet.csv'
  ): Promise<CsvExportResult> {
    try {
      // Fetch the data using ChartService
      const data = await this.chartService.getToolData(sessionId, dataId, userEmail, token);
      
      if (!data) {
        return {
          success: false,
          filename,
          error: 'Data not found for the selected data ID',
        };
      }

      // Convert to CSV
      const csvContent = this.convertToCsv(data);
      
      if (!csvContent) {
        return {
          success: false,
          filename,
          error: 'No data available to export',
        };
      }

      // Trigger download
      this.downloadCsv(csvContent, filename);

      return {
        success: true,
        filename,
      };
    } catch (error) {
      logger.error('Error exporting to CSV:', error);
      return {
        success: false,
        filename,
        error: error instanceof Error ? error.message : 'Failed to export data',
      };
    }
  }

  /**
   * Export all data_ids combined into a single CSV, joined on dates.
   * Each dataset becomes a column with a descriptive name.
   */
  async exportCombinedToCsv(
    sessionId: string,
    dataIds: Record<string, string>,
    userEmail: string,
    token: string,
    filename: string = 'quantic_datasheet_combined.csv'
  ): Promise<CsvExportResult> {
    try {
      const entries = buildEnglishLabels(
        Object.entries(dataIds).map(([dataId, rawDescription]) => ({
          dataId,
          rawDescription,
        }))
      );
      if (entries.length === 0) {
        return { success: false, filename, error: 'No data to export' };
      }

      // Fetch all datasets and build labels from descriptions
      const dataSources: Array<{ data: ToolDataResponse; label: string }> = [];
      for (const { dataId, label } of entries) {
        const data = await this.chartService.getToolData(sessionId, dataId, userEmail, token);
        if (data) {
          dataSources.push({ data, label });
        }
      }

      if (dataSources.length === 0) {
        return { success: false, filename, error: 'Failed to fetch data' };
      }

      // Merge all datasets on dates using ChartService
      const merged = this.chartService.mergeDataByDate(dataSources);

      // Convert merged data to CSV
      const csvContent = this.convertToCsv(merged as unknown as ToolDataResponse);
      if (!csvContent) {
        return { success: false, filename, error: 'No data available to export' };
      }

      this.downloadCsv(csvContent, filename);
      return { success: true, filename };
    } catch (error) {
      logger.error('Error exporting combined CSV:', error);
      return {
        success: false,
        filename,
        error: error instanceof Error ? error.message : 'Failed to export combined data',
      };
    }
  }
}

// Export singleton instance
export const csvExportService = new CsvExportService();
